import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Trash2, RotateCcw, RefreshCw, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PERMANENT_CONFIRM = "HAPUS PERMANEN";

export default function RecycleBinPage() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [permanentTarget, setPermanentTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/voters/deleted", { params: q.trim() ? { q: q.trim() } : {} });
      setData(data);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const restoreOne = async (v) => {
    try {
      await api.post(`/admin/voters/${v.id}/restore`);
      toast.success(`${v.nama} berhasil di-restore.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkRestore = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Restore ${selected.size} pemilih terpilih?`)) return;
    try {
      const { data } = await api.post("/admin/voters/bulk-restore", { voter_ids: [...selected] });
      toast.success(`Restored ${data.restored} dari ${data.requested} pemilih.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const permanentDelete = async () => {
    if (!permanentTarget) return;
    if (confirmText !== PERMANENT_CONFIRM) {
      toast.error(`Ketik "${PERMANENT_CONFIRM}" tepat sesuai.`);
      return;
    }
    try {
      await api.delete(`/admin/voters/${permanentTarget.id}/permanent`, {
        data: { confirm: PERMANENT_CONFIRM },
      });
      toast.success(`${permanentTarget.nama} dihapus permanen.`);
      setPermanentTarget(null);
      setConfirmText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredItems = useMemo(() => data.items, [data.items]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredItems.length) setSelected(new Set());
    else setSelected(new Set(filteredItems.map((v) => v.id)));
  };

  return (
    <div className="space-y-6" data-testid="recycle-bin-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Admin</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <Trash2 className="h-8 w-8 text-slate-400" /> Recycle Bin
          </h1>
          <p className="text-slate-600 text-sm mt-2 max-w-2xl">
            Pemilih yang di-soft-delete. Bisa di-restore kembali atau dihapus permanen.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="btn-refresh-bin">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </header>

      <Card>
        <CardContent className="pt-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Total di Recycle Bin</div>
            <div className="text-3xl font-bold tabular-nums" data-testid="total-deleted">{data.total.toLocaleString("id-ID")}</div>
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <Button
                onClick={bulkRestore}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-bulk-restore"
              >
                <RotateCcw className="h-4 w-4 mr-2" /> Restore {selected.size} pemilih
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Cari nama atau NIK…"
            className="pl-9"
            data-testid="input-search-deleted"
          />
        </div>
        <Button variant="outline" onClick={load}>Cari</Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="p-3 w-10 text-left">
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === filteredItems.length}
                  onChange={toggleAll}
                  data-testid="check-all"
                />
              </th>
              <th className="p-3 text-left">Nama</th>
              <th className="p-3 text-left">NIK</th>
              <th className="p-3 text-left">TPS · RT/RW</th>
              <th className="p-3 text-left">Dihapus</th>
              <th className="p-3 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((v) => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`row-${v.id}`}>
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selected.has(v.id)}
                    onChange={() => toggleSelect(v.id)}
                    data-testid={`check-${v.id}`}
                  />
                </td>
                <td className="p-3 font-medium">{v.nama}</td>
                <td className="p-3 font-mono text-xs text-slate-600">{v.nik || "-"}</td>
                <td className="p-3 text-xs text-slate-600">
                  <div>{v.tps || "-"}</div>
                  <div className="text-slate-400">{v.rt}/{v.rw} · {v.dusun}</div>
                </td>
                <td className="p-3 text-xs">
                  <div className="text-slate-700">{v.deleted_at ? new Date(v.deleted_at).toLocaleString("id-ID") : "-"}</div>
                  <div className="text-slate-400">oleh {v.deleted_by_username || "?"}</div>
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      onClick={() => restoreOne(v)}
                      data-testid={`btn-restore-${v.id}`}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" /> Restore
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => setPermanentTarget(v)}
                      data-testid={`btn-permanent-${v.id}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Permanen
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-400 text-sm">
                  Recycle Bin kosong.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Permanent delete confirmation dialog */}
      <Dialog open={!!permanentTarget} onOpenChange={(v) => { if (!v) { setPermanentTarget(null); setConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="permanent-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Hapus Permanen
            </DialogTitle>
          </DialogHeader>
          {permanentTarget && (
            <div className="space-y-4 text-sm">
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-red-800">
                Aksi ini <b>TIDAK BISA DIURUNGKAN</b>. Data pemilih akan hilang dari database selamanya.
              </div>
              <div className="rounded-md bg-slate-50 p-3">
                <div className="text-slate-500 text-[11px] uppercase font-semibold">Target</div>
                <div className="font-semibold">{permanentTarget.nama}</div>
                <div className="text-xs text-slate-600 font-mono">{permanentTarget.nik}</div>
                <div className="text-xs text-slate-500">{permanentTarget.tps} · RT {permanentTarget.rt}/RW {permanentTarget.rw} · {permanentTarget.dusun}</div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">
                  Ketik <span className="font-mono text-red-700">{PERMANENT_CONFIRM}</span> untuk konfirmasi:
                </label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={PERMANENT_CONFIRM}
                  className="font-mono"
                  data-testid="input-confirm-permanent"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPermanentTarget(null); setConfirmText(""); }}>Batal</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={confirmText !== PERMANENT_CONFIRM}
              onClick={permanentDelete}
              data-testid="btn-confirm-permanent"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Hapus Permanen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
